import React, { useState } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useMenuItems, useMenuCategories, type MenuItem, type MenuCategory } from "../hooks/useMenu";

// Fallback data that matches the current website's content
const fallbackCategories = [
  { id: "1", name: "Hookah", description: "Premium tobacco blends and flavors" },
  { id: "2", name: "Food", description: "Delicious appetizers and main courses" },
  { id: "3", name: "Drinks", description: "Signature cocktails and beverages" }
];

const fallbackMenuItems = {
  "1": [ // Hookah
    { id: "1", name: "Classic Mint", description: "Refreshing mint flavor with a cool finish", price: 25, is_featured: true },
    { id: "2", name: "Double Apple", description: "Traditional blend with rich apple notes", price: 25, is_featured: false },
    { id: "3", name: "Blue Mist", description: "Sweet blueberry flavor with a hint of mint", price: 28, is_featured: true },
    { id: "4", name: "Watermelon Chill", description: "Juicy watermelon with a cooling effect", price: 28, is_featured: false },
    { id: "5", name: "Citrus Paradise", description: "Mix of zesty orange, lemon and lime", price: 28, is_featured: false },
    { id: "6", name: "Seven8Nine Special", description: "Our signature house blend (ask server for details)", price: 30, is_featured: true }
  ],
  "2": [ // Food
    { id: "7", name: "Hummus Plate", description: "Creamy hummus with warm pita bread and olive oil", price: 12, is_featured: true },
    { id: "8", name: "Falafel Platter", description: "Crispy falafel served with tahini sauce", price: 14, is_featured: false },
    { id: "9", name: "Chicken Shawarma", description: "Tender marinated chicken with garlic sauce and pickles", price: 16, is_featured: true },
    { id: "10", name: "Mixed Grill", description: "Selection of grilled meats with rice and vegetables", price: 24, is_featured: false },
    { id: "11", name: "Vegetarian Platter", description: "Assortment of vegetarian mezze items", price: 18, is_featured: false },
    { id: "12", name: "Baklava", description: "Sweet pastry with layers of nuts and honey", price: 8, is_featured: false }
  ],
  "3": [ // Drinks
    { id: "13", name: "Moroccan Mint Tea", description: "Traditional mint tea served in authentic teapot", price: 6, is_featured: true, tags: ["Tea"] },
    { id: "14", name: "Turkish Coffee", description: "Strong coffee brewed in a cezve with cardamom", price: 5, is_featured: false, tags: ["Coffee"] },
    { id: "15", name: "Lounge Lemonade", description: "Fresh lemonade with mint and rose water", price: 7, is_featured: true, tags: ["Mocktail"] },
    { id: "16", name: "Mango Smoothie", description: "Refreshing mango blend with a hint of honey", price: 8, is_featured: false, tags: ["Smoothie"] },
    { id: "17", name: "Seven8Nine Signature Mocktail", description: "Our special non-alcoholic house blend", price: 9, is_featured: true, tags: ["Mocktail"] },
    { id: "18", name: "Sparkling Water", description: "Premium sparkling water with or without flavor", price: 4, is_featured: false, tags: ["Water"] }
  ]
};

// Map category name to image file
const categoryImages: Record<string, string> = {
  Hookah: '/assets/Hookah Image.png',
  Food: '/assets/Food Image.png',
  Drinks: '/assets/Drinks Image.png',
};

const Menu: React.FC = () => {
  const { data: dbCategories = [] } = useMenuCategories();
  const { data: dbMenuItems = [] } = useMenuItems();
  
  // Use database categories if available, otherwise use fallback
  const categories = dbCategories.length > 0 ? dbCategories : fallbackCategories;
  
  const [activeCategory, setActiveCategory] = useState<string>(categories[0]?.id || "1");

  // Get active items - use database items if available, otherwise use fallback
  const getActiveItems = () => {
    if (dbMenuItems.length > 0) {
      return dbMenuItems.filter(item => item.category_id === activeCategory);
    }
    return fallbackMenuItems[activeCategory as keyof typeof fallbackMenuItems] || [];
  };

  // Get category name
  const getActiveCategoryName = () => {
    const category = categories.find(cat => cat.id === activeCategory);
    return category?.name || "";
  };

  const activeItems = getActiveItems();

  return (
    <div className="min-h-screen bg-lounge-dark text-lounge-light">
      <Navbar />

      {/* Page Header */}
      <div className="relative pt-20 pb-16 md:pt-24 md:pb-20 bg-lounge-charcoal">
        <div className="container-custom">
          <h1 className="section-title text-center">Our Menu</h1>
          <p className="section-subtitle text-center max-w-3xl mx-auto">
            Explore our selection of premium hookah flavors, delicious food, and refreshing drinks.
          </p>
        </div>
      </div>

      {/* Menu Categories Navigation */}
      <div className="sticky top-0 z-20 bg-lounge-dark/95 backdrop-blur-sm border-b border-lounge-gold/20">
        <div className="container-custom py-4">
          <div className="flex justify-center space-x-2 md:space-x-8">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`px-4 py-2 font-display font-semibold transition-colors ${
                  activeCategory === category.id ? "text-lounge-gold border-b-2 border-lounge-gold" : "text-lounge-light/70 hover:text-lounge-gold"
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Menu Items */}
      <section className="section-padding bg-lounge-dark">
        <div className="container-custom">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl font-display font-bold mb-8 text-lounge-gold text-center">
              {getActiveCategoryName()}
            </h2>

            <div className="flex flex-wrap -mx-4">
              {activeItems.map((item) => (
                <div 
                  key={item.id} 
                  className="w-full px-4 mb-8 sm:w-1/2 md:w-1/3"
                >
                  <div className={`h-full bg-lounge-charcoal p-6 rounded-lg border ${
                    item.is_featured ? "border-lounge-gold/40" : "border-lounge-gold/10"
                  }`}>
                    <div className="flex justify-between items-start gap-4 mb-4">
                      <h3 className="text-xl font-display font-semibold text-lounge-gold flex items-center flex-wrap">
                        {item.name}
                        {item.is_featured && (
                          <span className="ml-2 bg-lounge-gold/20 text-lounge-gold text-xs px-2 py-1 rounded-full">
                            Popular
                          </span>
                        )}
                      </h3>
                      <div className="text-lounge-gold font-display text-xl font-semibold whitespace-nowrap">
                        ${item.price}
                      </div>
                    </div>
                    <p className="text-lounge-light/70">{item.description}</p>
                    {/* Tags */}
                    {Array.isArray(item.tags) && item.tags.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-4">
                        {item.tags.map((tag: string) => (
                          <span
                            key={tag}
                            className="bg-lounge-gold/10 text-lounge-gold text-xs px-2 py-0.5 rounded-full font-semibold"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-12 p-6 bg-lounge-charcoal rounded-lg border border-lounge-gold/20">
              <h3 className="text-xl font-display font-semibold text-lounge-gold mb-4">Special Requests</h3>
              <p className="text-lounge-light/80">
                Have a special request or dietary restriction? Our staff is happy to accommodate your needs whenever possible. Please inform your server, and we'll do our best to serve you.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Menu;
